import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CollectionCard } from "./CollectionCard";
import { CreateCollectionDialog } from "./CreateCollectionDialog";
import { useCollections, useDeleteCollection, useUpdateCollection } from "@/hooks/collections";
import { useAuth } from "@/context/AuthContext";
import type { PromptCollection } from "@/lib/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CollectionGridProps {
  userId?: string;
  isOwnProfile?: boolean;
}

export function CollectionGrid({ userId, isOwnProfile = false }: CollectionGridProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: collections, isLoading } = useCollections(userId);
  const deleteCollection = useDeleteCollection();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<PromptCollection | null>(null);
  const [deletingCollection, setDeletingCollection] = useState<PromptCollection | null>(null);

  const handleCollectionClick = (collection: PromptCollection) => {
    navigate(`/collections/${collection.id}`);
  };

  const handleDelete = () => {
    if (deletingCollection) {
      deleteCollection.mutate(deletingCollection.id, {
        onSuccess: () => setDeletingCollection(null),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-muted/50 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (!collections?.length) {
    return (
      <div className="text-center py-12">
        <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Collections Yet</h3>
        <p className="text-muted-foreground mb-4">
          {isOwnProfile
            ? "Create collections to organize your prompts for easy access."
            : "This user hasn't created any public collections yet."}
        </p>
        {isOwnProfile && (
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Collection
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isOwnProfile && (
        <div className="flex justify-end">
          <Button onClick={() => setCreateDialogOpen(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Collection
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {collections.map((collection) => (
          <CollectionCard
            key={collection.id}
            collection={collection}
            onClick={() => handleCollectionClick(collection)}
            onEdit={() => setEditingCollection(collection)}
            onDelete={() => setDeletingCollection(collection)}
            isOwner={isOwnProfile}
          />
        ))}
      </div>

      {/* Create Dialog */}
      <CreateCollectionDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {/* Edit Dialog */}
      {editingCollection && (
        <CreateCollectionDialog
          open={!!editingCollection}
          onOpenChange={(open) => !open && setEditingCollection(null)}
          editingCollection={editingCollection}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingCollection} onOpenChange={(open) => !open && setDeletingCollection(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Collection?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deletingCollection?.name}". The prompts inside won't be deleted, just removed from this collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
